import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { ChevronDown, X } from "lucide-react";
import { Country } from "country-state-city";
import useCrmApi from "../../../components/hook/crm-req";
import Input from "../../../components/input";
import { UserResponse } from "../../../components/types";
import { useCreateLeadQrCampaign, useListLeadQrCampaigns, useUpdateLeadQrCampaign } from "./qr-lead-api";
import { defaultLeadQrCampaign, LeadQrCampaign, LeadQrFormField } from "./qr-lead-types";

// Same derivation as the "Add New Contact" phone field
// (src/components/crm/customer/update-customer.tsx) — dial code + ISO label,
// deduped and sorted.
const normalizeDialCode = (phoneCode?: string | number | null): string => {
  if (!phoneCode) return "+234";
  const code = String(phoneCode);
  return code.startsWith("+") ? code : `+${code}`;
};
const COUNTRY_CODE_OPTIONS = Country.getAllCountries()
  .map((country) => ({
    value: normalizeDialCode(country.phonecode),
    label: `${normalizeDialCode(country.phonecode)} ${country.isoCode}`,
  }))
  .filter((country, index, list) => list.findIndex((item) => item.value === country.value) === index)
  .sort((a, b) => a.value.localeCompare(b.value));

// ─── Small helpers ──────────────────────────────────────────────────────────

// GET /leads response shape (confirmed 2026-07-31) — flat fields (source,
// tags, updated_at all top-level, not nested under `details` like the older
// /crm/unified shape LeadRecord/mapRecordToLead was built for), with both
// snake_case and camelCase duplicates. Only what "Offline Leads" needs.
interface OfflineLead {
  id: string;
  name: string;
  phone: string | null;
  email: string | null;
  source: string | null;
  updatedAt: string | null;
}

const mapOfflineLead = (r: any): OfflineLead => ({
  id: r.id,
  name: r.display_name || r.displayName || `${r.first_name || ""} ${r.last_name || ""}`.trim() || "Unknown",
  phone: r.phone || null,
  email: r.email || null,
  source: r.source || null,
  updatedAt: r.updated_at || r.updatedAt || null,
});

const relativeTime = (iso: string | null): string => {
  if (!iso) return "—";
  const diffMs = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diffMs / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}min ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  return `${Math.floor(hours / 24)}d ago`;
};

const csvCell = (value: unknown): string => {
  const str = value == null ? "" : String(value);
  return /[",\n]/.test(str) ? `"${str.replace(/"/g, '""')}"` : str;
};

const downloadBlob = (blob: Blob, filename: string) => {
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

// Fields shown in "Customise Lead Form" — key/type/required are fixed
// (matches the design), only each field's placeholder is business-editable.
const CONTACT_FIELD_DEFS: Pick<LeadQrFormField, "key" | "label" | "type" | "required">[] = [
  { key: "first_name", label: "First Name", type: "text", required: true },
  { key: "last_name", label: "Last Name", type: "text", required: false },
  { key: "phone", label: "Phone/ WhatsApp", type: "tel", required: true },
  { key: "company_name", label: "Company Name", type: "text", required: false },
  { key: "company_website", label: "Company Website", type: "text", required: false },
  { key: "role", label: "Role", type: "text", required: false },
  { key: "email", label: "Email Address", type: "email", required: false },
];

// ─── Customise Lead Form modal ──────────────────────────────────────────────

const CustomiseFormModal: React.FC<{
  user: UserResponse | null;
  campaign: LeadQrCampaign | null;
  onClose: () => void;
  onSaved: (campaign: LeadQrCampaign) => void;
}> = ({ user, campaign, onClose, onSaved }) => {
  const { makeRequest: createCampaign } = useCreateLeadQrCampaign();
  const { makeRequest: updateCampaign } = useUpdateLeadQrCampaign();
  const [saving, setSaving] = useState(false);

  // Starts empty — fields show plain instructional placeholder text (via the
  // HTML placeholder attribute) rather than any pre-filled value. Only an
  // already-saved campaign's own real placeholder is loaded as an actual
  // value, since that's genuine existing data, not invented copy.
  const seedPlaceholder = (key: string): string => campaign?.form_fields.find((f) => f.key === key)?.placeholder || "";

  const [placeholders, setPlaceholders] = useState<Record<string, string>>(() =>
    CONTACT_FIELD_DEFS.reduce((acc, f) => ({ ...acc, [f.key]: seedPlaceholder(f.key) }), {}),
  );

  // Dialing code from the business's own account location, not a hardcoded
  // country — falls back to Nigeria only if the profile has none set. Used
  // as the default selection for both country-code dropdowns below.
  const userCountryCode = `+${user?.payload?.country_code || 234}`;
  const existingTarget = campaign?.action_type === "whatsapp" ? campaign.action_target || "" : "";
  // Best-effort split of an already-saved number back into code + local part
  // — if no known dial code matches the start of it, default the dropdown to
  // the business's own country and show the full saved value in the input
  // rather than risk mangling real saved data.
  const matchedWhatsappCode = [...COUNTRY_CODE_OPTIONS]
    .sort((a, b) => b.value.length - a.value.length)
    .find((o) => existingTarget.startsWith(o.value));
  const [whatsappCountryCode, setWhatsappCountryCode] = useState(matchedWhatsappCode?.value || userCountryCode);
  const [whatsappNumber, setWhatsappNumber] = useState(
    matchedWhatsappCode ? existingTarget.slice(matchedWhatsappCode.value.length) : existingTarget,
  );

  // The "Phone/ WhatsApp" contact-detail field's example placeholder — same
  // country-code dropdown + local-number pattern as "Add New Contact"
  // (update-customer.tsx), defaulting to the business's own country code.
  // Composes into that field's `placeholder` string on save; left blank (no
  // placeholder saved) if the business never types an example number.
  const existingPhonePlaceholder = campaign?.form_fields.find((f) => f.key === "phone")?.placeholder || "";
  const [phoneCountryCode, setPhoneCountryCode] = useState(
    existingPhonePlaceholder.startsWith("+")
      ? COUNTRY_CODE_OPTIONS.find((o) => existingPhonePlaceholder.startsWith(o.value))?.value || userCountryCode
      : userCountryCode,
  );
  const [phoneLocalNumber, setPhoneLocalNumber] = useState(
    existingPhonePlaceholder.startsWith(phoneCountryCode) ? existingPhonePlaceholder.slice(phoneCountryCode.length) : "",
  );

  const orgName = user?.payload?.org_name || "your business";

  const handleSave = async () => {
    for (const f of CONTACT_FIELD_DEFS.filter((field) => field.required)) {
      const filled = f.key === "phone" ? !!phoneLocalNumber.trim() : !!placeholders[f.key]?.trim();
      if (!filled) {
        toast.error(`${f.label} is required.`);
        return;
      }
    }
    if (!whatsappNumber.trim()) {
      toast.error("Enter the WhatsApp number that should receive leads.");
      return;
    }
    const fullWhatsappNumber = whatsappNumber.trim().startsWith("+")
      ? whatsappNumber.trim()
      : `${userCountryCode}${whatsappNumber.trim()}`;
    setSaving(true);
    try {
      const formFields: LeadQrFormField[] = CONTACT_FIELD_DEFS.map((f) => ({
        key: f.key,
        label: f.label,
        type: f.type,
        required: f.required,
        placeholder: f.key === "phone" ? (phoneLocalNumber.trim() ? `${phoneCountryCode}${phoneLocalNumber.trim()}` : null) : placeholders[f.key] || null,
      }));

      if (!campaign) {
        const input = {
          ...defaultLeadQrCampaign,
          name: `${orgName} Lead Form QR`,
          action_type: "whatsapp" as const,
          action_target: fullWhatsappNumber,
          form_fields: formFields,
        };
        const [response, status] = await createCampaign(input);
        if (status < 200 || status >= 300) {
          toast.error(response?.detail || "Failed to create the QR lead form.");
          return;
        }
        onSaved(response);
        toast.success("QR lead form created.");
      } else {
        const payload = {
          action_type: "whatsapp" as const,
          action_target: fullWhatsappNumber,
          form_fields: formFields,
        };
        const [response, status] = await updateCampaign(payload, undefined, campaign.id);
        if (status < 200 || status >= 300) {
          toast.error(response?.detail || "Failed to save changes.");
          return;
        }
        onSaved(response);
        toast.success("Lead form updated.");
      }
      onClose();
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40">
      <div className="bg-white dark:bg-gray-900 rounded-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto shadow-xl">
        <div className="flex items-start justify-between px-6 py-5 border-b border-gray-100 dark:border-gray-800">
          <div>
            <h2 className="text-base font-semibold text-gray-900 dark:text-gray-100">Customise Lead Form</h2>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
              Customise the information shown to visitors before they share their details.
            </p>
          </div>
          <button onClick={onClose} className="text-[#7B7B7B] font-semibold dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 bg-transparent border-none cursor-pointer">
            <X size={18} />
          </button>
        </div>

        <div className="px-6 py-5 space-y-5">
          <div>
            <p className="text-xs font-semibold text-indigo-600 dark:text-indigo-400 mb-3">Your Contact Details</p>
            <div className="grid grid-cols-2 gap-3">
              {CONTACT_FIELD_DEFS.slice(0, 2).map((f) => (
                <FieldInput key={f.key} field={f} value={placeholders[f.key]} onChange={(v) => setPlaceholders((p) => ({ ...p, [f.key]: v }))} />
              ))}
            </div>
            <div className="mt-3">
              <label className="text-xs font-medium text-gray-600 dark:text-gray-400 mb-1 block">Phone/ WhatsApp (required)</label>
              <div className="flex gap-2">
                <div className="relative w-28 shrink-0">
                  <select
                    value={phoneCountryCode}
                    onChange={(e) => setPhoneCountryCode(e.target.value)}
                    className="w-full appearance-none pl-3 pr-7 py-2 text-sm rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 outline-none text-gray-700 dark:text-gray-300"
                  >
                    {COUNTRY_CODE_OPTIONS.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                  <ChevronDown size={13} className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 text-[#7B7B7B] font-semibold dark:text-gray-500" />
                </div>
                <input
                  type="tel"
                  value={phoneLocalNumber}
                  onChange={(e) => setPhoneLocalNumber(e.target.value)}
                  placeholder="Enter Phone/ WhatsApp"
                  className="flex-1 px-3 py-2 border border-gray-200 dark:border-gray-700 rounded-lg text-sm outline-none focus:ring-2 focus:ring-indigo-200 focus:border-indigo-400"
                />
              </div>
              {!phoneLocalNumber.trim() && <span className="text-[#FF0101] dark:text-red-400 text-[12px] mt-1 block">This field is required</span>}
            </div>
            <div className="grid grid-cols-2 gap-3 mt-3">
              {CONTACT_FIELD_DEFS.slice(3, 5).map((f) => (
                <FieldInput key={f.key} field={f} value={placeholders[f.key]} onChange={(v) => setPlaceholders((p) => ({ ...p, [f.key]: v }))} />
              ))}
            </div>
            <div className="grid grid-cols-2 gap-3 mt-3">
              {CONTACT_FIELD_DEFS.slice(5, 7).map((f) => (
                <FieldInput key={f.key} field={f} value={placeholders[f.key]} onChange={(v) => setPlaceholders((p) => ({ ...p, [f.key]: v }))} />
              ))}
            </div>
          </div>

          <label className="flex items-start gap-2 text-xs text-gray-500 dark:text-gray-400">
            <input type="checkbox" checked readOnly className="mt-0.5 accent-indigo-600" />
            I agree to share my details with {orgName} so they can contact me about my enquiry.
          </label>

          <div className="bg-indigo-50/60 dark:bg-indigo-500/10 border border-indigo-100 dark:border-indigo-500/30 rounded-xl p-4 space-y-3">
            <div>
              <label className="text-xs font-medium text-gray-600 dark:text-gray-400 mb-1 block">
                Enter the WhatsApp number to receive lead info
              </label>
              <div className="flex gap-2">
                <div className="relative w-28 shrink-0">
                  <select
                    value={whatsappCountryCode}
                    onChange={(e) => setWhatsappCountryCode(e.target.value)}
                    className="w-full appearance-none pl-3 pr-7 py-2 text-sm rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 outline-none text-gray-700 dark:text-gray-300"
                  >
                    {COUNTRY_CODE_OPTIONS.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                  <ChevronDown size={13} className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 text-[#7B7B7B] font-semibold dark:text-gray-500" />
                </div>
                <input
                  value={whatsappNumber}
                  onChange={(e) => setWhatsappNumber(e.target.value)}
                  placeholder="8989893800"
                  className="flex-1 px-3 py-2 border border-gray-200 dark:border-gray-700 rounded-lg text-sm outline-none focus:ring-2 focus:ring-indigo-200 focus:border-indigo-400"
                />
              </div>
            </div>
          </div>
        </div>

        <div className="px-6 py-5 border-t border-gray-100 dark:border-gray-800 space-y-2">
          <button
            onClick={handleSave}
            disabled={saving}
            className="w-full py-2.5 bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-semibold rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {saving ? "Saving..." : "Save Form"}
          </button>
          <button
            onClick={onClose}
            className="w-full py-2.5 border border-gray-200 dark:border-gray-700 rounded-lg text-sm text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
};

const FieldInput: React.FC<{
  field: Pick<LeadQrFormField, "key" | "label" | "required">;
  value: string;
  onChange: (v: string) => void;
}> = ({ field, value, onChange }) => (
  <Input
    label={`${field.label} (${field.required ? "required" : "optional"})`}
    value={value}
    onChange={(e) => onChange(e.target.value)}
    placeholder={`Enter ${field.label}`}
    required={field.required}
  />
);

// ─── Main section ───────────────────────────────────────────────────────────

export const LeadQrSection: React.FC<{ user: UserResponse | null }> = ({ user }) => {
  const { makeRequest: listCampaigns } = useListLeadQrCampaigns();
  const { makeRequest: getUnifiedLeads } = useCrmApi("/leads", "GET", {
    Authorization: `Bearer ${localStorage.getItem("authToken")}`,
  });

  const [campaign, setCampaign] = useState<LeadQrCampaign | null>(null);
  const [loadingCampaign, setLoadingCampaign] = useState(true);
  const [cardSide, setCardSide] = useState<"front" | "back">("front");
  const [showModal, setShowModal] = useState(false);

  const [qrLeads, setQrLeads] = useState<OfflineLead[]>([]);
  const [qrLeadsLoading, setQrLeadsLoading] = useState(true);

  useEffect(() => {
    (async () => {
      setLoadingCampaign(true);
      try {
        const [response, status] = await listCampaigns();
        if (status >= 200 && status < 300) {
          const list: LeadQrCampaign[] = Array.isArray(response) ? response : response?.data || [];
          setCampaign(list[0] || null);
        }
      } finally {
        setLoadingCampaign(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    (async () => {
      setQrLeadsLoading(true);
      try {
        const [response, status] = await getUnifiedLeads(undefined, { tag: "qr_code", sort_order: "desc", page: 1, per_page: 100 });
        if (status >= 200 && status < 300 && Array.isArray(response?.results)) {
          const mapped = response.results.map(mapOfflineLead).filter((l: OfflineLead) => l.source === "qr_code");
          setQrLeads(mapped);
        }
      } finally {
        setQrLeadsLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const previewUrl = campaign ? `${campaign.qr_image_url}?v=${encodeURIComponent(campaign.updated_at)}` : null;

  const handleDownloadCard = async () => {
    if (!campaign) return;
    try {
      const res = await fetch(campaign.qr_image_url);
      const svgText = await res.text();

      // The backend only serves this as SVG, but "download the QR code"
      // means a PNG people can actually open/share/print anywhere — render
      // the SVG onto a canvas and export that instead. Data URL (not the
      // remote image URL) so the canvas never gets cross-origin-tainted.
      const svgDataUrl = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svgText)}`;
      const pngBlob = await new Promise<Blob | null>((resolve, reject) => {
        const img = new Image();
        img.onload = () => {
          // Rendered well above the ~112px on-screen preview so the
          // exported code stays crisp/scannable at print size too.
          const size = 1024;
          const canvas = document.createElement("canvas");
          canvas.width = size;
          canvas.height = size;
          const ctx = canvas.getContext("2d");
          if (!ctx) {
            reject(new Error("Canvas not supported"));
            return;
          }
          ctx.fillStyle = "#ffffff";
          ctx.fillRect(0, 0, size, size);
          ctx.drawImage(img, 0, 0, size, size);
          canvas.toBlob(resolve, "image/png");
        };
        img.onerror = () => reject(new Error("Could not load the QR code image"));
        img.src = svgDataUrl;
      });
      if (!pngBlob) throw new Error("Could not render the QR code");

      downloadBlob(pngBlob, `${user?.payload?.org_name || "lead"}-qr-code.png`);
    } catch {
      toast.error("Could not download the QR code right now.");
    }
  };

  const handleExportQrLeads = () => {
    if (qrLeads.length === 0) {
      toast.info("No offline leads to export yet.");
      return;
    }
    const header = ["Name", "Phone", "Email", "Updated"];
    const rows = qrLeads.map((l) => [l.name, l.phone, l.email, l.updatedAt].map(csvCell).join(","));
    const csv = [header.join(","), ...rows].join("\n");
    downloadBlob(new Blob([csv], { type: "text/csv;charset=utf-8;" }), `offline-leads_${new Date().toISOString().split("T")[0]}.csv`);
  };

  const whatsappNumber = campaign?.action_type === "whatsapp" ? campaign.action_target : null;
  const roleLine = user?.payload?.role
    ? `${user.payload.role}${user?.payload?.org_name ? ` at ${user.payload.org_name}` : ""}`
    : user?.payload?.org_name || "";

  return (
    <section>
      <h2 className="text-base font-semibold text-gray-800 dark:text-gray-100 mb-3">Get Leads on the Go</h2>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* ── QR code card ── */}
        <div className="bg-white dark:bg-gray-900 rounded-2xl p-5 shadow-sm border border-gray-100 dark:border-gray-800 flex flex-col">
          <div className="flex-1 flex flex-col items-center justify-center text-center py-4">
            {loadingCampaign ? (
              <p className="text-xs text-[#7B7B7B] font-semibold dark:text-gray-500">Loading…</p>
            ) : previewUrl ? (
              <>
                <img src={previewUrl} alt="Lead QR code" className="w-28 h-28" />
                <p className="text-xs text-[#7B7B7B] font-semibold dark:text-gray-400 mt-3 max-w-[220px]">
                  Share this unique QR to collect lead details and sync every lead directly to your ConvoCRM
                </p>
              </>
            ) : (
              <p className="text-xs text-[#7B7B7B] font-semibold dark:text-gray-400 max-w-[220px]">
                Set up your lead form to get your unique QR code.
              </p>
            )}
          </div>
          <div className="space-y-2 mt-2">
            <button
              onClick={() => campaign && window.open(campaign.form_url, "_blank", "noopener,noreferrer")}
              disabled={!campaign}
              className="w-full py-2 border border-gray-200 dark:border-gray-700 rounded-lg text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              Preview as a Visitor ↗
            </button>
            <button
              onClick={campaign ? handleDownloadCard : () => setShowModal(true)}
              className="w-full py-2 border border-gray-200 dark:border-gray-700 rounded-lg text-sm font-semibold text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
            >
              {campaign ? "Download your Card ↓" : "Set Up Now"}
            </button>
          </div>
          <div className="mt-4 pt-4 border-t border-gray-100 dark:border-gray-800">
            <p className="text-xs font-semibold text-gray-600 dark:text-gray-400 mb-2">How it works</p>
            <ol className="space-y-1.5 text-[12px] text-[#7B7B7B] font-semibold dark:text-gray-500 list-decimal list-inside">
              <li>Download and share your unique QR code</li>
              <li>Your visitors scan the code and fill your form</li>
              <li>Leads are directly saved in your ConvoCRM</li>
            </ol>
          </div>
        </div>

        {/* ── Your Card ── */}
        <div className="bg-white dark:bg-gray-900 rounded-2xl p-5 shadow-sm border border-gray-100 dark:border-gray-800 flex flex-col">
          <p className="text-sm font-semibold text-gray-900 dark:text-gray-100">Your Card</p>
          <p className="text-[12px] text-[#7B7B7B] font-semibold dark:text-gray-500 mt-1 mb-3">
            This is your digital business card. Customize the lead form visitors complete when sharing their details.
          </p>

          <div className="flex items-center gap-1.5 mb-2">
            {user?.payload?.org_logo && <img src={user.payload.org_logo} alt="" className="w-5 h-5 rounded-full" />}
            <span className="text-sm font-semibold text-gray-800 dark:text-gray-100">{user?.payload?.org_name}</span>
          </div>


          <div className="flex-1 bg-[#F6F5F5] dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700 p-5 flex flex-col  text-left gap-1">

            {cardSide === "back" ? (
              previewUrl ? (
                <div className="flex justify-center items-center">
                <img src={previewUrl} alt="QR" className="w-24 h-24 my-2" />
                </div>
              ) : (
                <p className="text-xs text-[#7B7B7B] font-semibold dark:text-gray-500 my-4">No QR code yet — set up your lead form.</p>
              )
            ) : null}

            <p className="text-[16px] text-center font-semibold text-black dark:text-gray-100 mt-1">
              {user?.payload?.first_name} {user?.payload?.last_name}
            </p>
            <p className="text-[12px] text-center text-[#434343] dark:text-gray-400 mb-2">{roleLine}</p>

            {cardSide === "front" && (
              <div className="w-full text-left text-black dark:text-gray-100 space-y-2 mt-2">
                {user?.payload?.email && (
                  <ContactRow label={user.payload.email} />
                )}
                {user?.payload?.phone_number && <ContactRow label={user.payload.phone_number} />}
                {(user?.payload?.domain_name || user?.payload?.org_domain_name) && (
                  <ContactRow label={user.payload.domain_name || user.payload.org_domain_name} />
                )}
                {whatsappNumber && (
                  <ContactRow label={whatsappNumber} caption="This is your WhatsApp number to receive leads" />
                )}
              </div>
            )}
          </div>

          <div className="flex items-center justify-center gap-1 bg-gray-100 dark:bg-gray-800 rounded-lg p-1 mt-3">
            {(["front", "back"] as const).map((side) => (
              <button
                key={side}
                onClick={() => setCardSide(side)}
                className={`flex-1 py-1.5 text-xs font-medium rounded-md capitalize transition-colors ${cardSide === side ? "bg-white dark:bg-gray-700 shadow-sm text-gray-900 dark:text-gray-100" : "text-gray-500 dark:text-gray-400"
                  }`}
              >
                {side}
              </button>
            ))}
          </div>

          <button
            onClick={() => setShowModal(true)}
            className="text-xs text-indigo-600 dark:text-indigo-400 hover:text-indigo-700 dark:hover:text-indigo-300 underline font-medium text-left mt-3"
          >
            Customise Form
          </button>
        </div>

        {/* ── Offline Leads ── */}
        <div className="bg-white dark:bg-gray-900 rounded-2xl p-5 shadow-sm border border-gray-100 dark:border-gray-800 flex flex-col">
          <div className="flex items-center justify-between mb-1">
            <p className="text-sm font-semibold text-gray-900 dark:text-gray-100">Offline Leads</p>
            <span className="text-xs bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400 font-semibold px-2 py-0.5 rounded-full">
              {qrLeads.length}
            </span>
          </div>
          <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">These are your recent offline leads</p>

          <div className="flex-1">
            {qrLeadsLoading ? (
              <p className="text-xs text-[#7B7B7B] font-semibold dark:text-gray-500 py-6 text-center">Loading…</p>
            ) : qrLeads.length === 0 ? (
              <p className="text-xs text-[#7B7B7B] font-semibold dark:text-gray-500 py-6 text-center">No offline leads yet.</p>
            ) : (
              qrLeads.slice(0, 5).map((lead) => (
                <div key={lead.id} className="flex items-center justify-between py-2 border-b border-gray-50 dark:border-gray-800 last:border-0">
                  <div className="min-w-0">
                    <p className="text-sm text-gray-800 dark:text-gray-100 capitalize truncate">{lead.name}</p>
                    <p className="text-xs text-[#7B7B7B] font-semibold dark:text-gray-500">{lead.phone || "—"}</p>
                  </div>
                  <span className="text-xs text-[#7B7B7B] font-semibold dark:text-gray-500 whitespace-nowrap ml-2">{relativeTime(lead.updatedAt)}</span>
                </div>
              ))
            )}
          </div>

          <div className="flex items-center justify-between pt-3 mt-2 border-t border-gray-100 dark:border-gray-800">
            <button onClick={handleExportQrLeads} className="text-xs text-indigo-600 dark:text-indigo-400 hover:text-indigo-700 dark:hover:text-indigo-300 underline font-medium">
              Export as CSV
            </button>
            <a href="/leads" className="text-xs text-indigo-600 dark:text-indigo-400 hover:text-indigo-700 dark:hover:text-indigo-300 underline font-medium">
              View All
            </a>
          </div>
        </div>
      </div>

      {showModal && (
        <CustomiseFormModal
          user={user}
          campaign={campaign}
          onClose={() => setShowModal(false)}
          onSaved={(c) => setCampaign(c)}
        />
      )}
    </section>
  );
};

const ContactRow: React.FC<{ label: string; caption?: string }> = ({ label, caption }) => (
  <div className="text-[12px]">
    <p className="text-black dark:text-gray-100 font-medium">{label}</p>
    {caption && <p className="text-[#6B7280] dark:text-gray-500 text-[10px]">{caption}</p>}
  </div>
);

export default LeadQrSection;
